import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { LoadingPanel } from '../../components/ui/Loader';
import { ApiError, api } from '../../lib/api';
import { useCan } from '../../stores/session';

interface Notice {
  id: number;
  reference: string;
  status: 'received' | 'valid' | 'actioned' | 'counter_filed' | 'withdrawn' | 'rejected';
  received_at: string;
  actioned_at: string | null;
  claimant: { name: string; email: string; org: string | null };
  work_description: string;
  signature: string;
  resolution_note: string | null;
  track: { id: number; title: string; owner: string; owner_name: string; on_hold: boolean } | null;
  counter: { id: number; statement: string; filed_at: string; forwarded_at: string | null } | null;
}

const TONE: Record<Notice['status'], string> = {
  received: 'text-accent',
  valid: 'text-accent',
  actioned: 'text-hot-text',
  counter_filed: 'text-fresh',
  withdrawn: 'text-faint',
  rejected: 'text-faint',
};

/**
 * The takedown queue (HANDOFF §11).
 *
 * ## Reading and deciding are different amounts of trust
 *
 * `dmca.view` gets you the list; `dmca.action` is what lets you silence
 * somebody's work, and moderators deliberately do not have it - a takedown is a
 * legal instrument with a counter-notice process attached, not a moderation
 * call. So this page renders for a moderator and simply does not offer the
 * buttons, rather than hiding the queue from them.
 *
 * ## The counter-notice is shown in full, next to the claim
 *
 * Because deciding whether to restore means reading both, and a page that
 * showed one and linked to the other would make the easy path the wrong one.
 */
export function AdminDmcaPage() {
  const canAction = useCan('dmca.action');
  const [filter, setFilter] = useState<string>('');

  const { data, isPending } = useQuery({
    queryKey: ['admin-dmca', filter],
    queryFn: () => api.get<{ items: Notice[] }>(`/admin/dmca${filter ? `?status=${filter}` : ''}`),
  });

  return (
    <div>

      <div className="mt-6 flex flex-wrap items-baseline justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Copyright claims</h1>
        <div className="flex flex-wrap gap-1.5">
          {[
            { id: '', label: 'All' },
            { id: 'received', label: 'New' },
            { id: 'actioned', label: 'Taken down' },
            { id: 'counter_filed', label: 'Answered' },
          ].map((option) => (
            <button
              key={option.id || 'all'}
              type="button"
              aria-pressed={filter === option.id}
              data-dmca-filter={option.id || 'all'}
              onClick={() => setFilter(option.id)}
              className={`meta-caps rounded-[var(--radius-pill)] border px-3 py-2 transition-colors ${
                filter === option.id
                  ? 'border-transparent bg-accent text-accent-ink'
                  : 'border-line text-soft hover:border-line-strong hover:text-ink'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {!canAction && (
        <Alert tone="info" >
          You can read the queue but not act on it. A takedown needs `dmca.action`.
        </Alert>
      )}

      {isPending ? (
        <LoadingPanel label="Loading claims" className="mt-8" />
      ) : (data?.items.length ?? 0) === 0 ? (
        <Card className="mt-8 p-8">
          <p className="text-sm text-soft">Nothing here.</p>
        </Card>
      ) : (
        <ul className="mt-6 flex flex-col gap-3">
          {data?.items.map((notice) => (
            <li key={notice.id}>
              <NoticeCard notice={notice} canAction={canAction} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NoticeCard({ notice, canAction }: { notice: Notice; canAction: boolean }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [strike, setStrike] = useState(true);

  const decide = useMutation({
    mutationFn: (decision: string) =>
      api.post(`/admin/dmca/${notice.id}/decision`, { decision, note: note.trim(), strike }),
    onSuccess: () => {
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['admin-dmca'] });
    },
  });

  const open = notice.status === 'received' || notice.status === 'valid';

  return (
    <Card className="flex flex-col gap-4 p-5" data-dmca-notice={notice.id}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <span className="flex items-center gap-3">
          <span className="font-mono text-[12px] text-ink">{notice.reference}</span>
          <span className={`meta-caps ${TONE[notice.status]}`}>
            {notice.status.replace('_', ' ')}
          </span>
          {notice.track?.on_hold && <Chip tone="hot">on hold</Chip>}
        </span>
        <span className="meta text-faint">{new Date(notice.received_at.replace(' ', 'T') + 'Z').toLocaleString()}</span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <span className="meta-caps text-faint">Claimant</span>
          <span className="text-sm text-ink">{notice.claimant.name}</span>
          <span className="font-mono text-[11px] text-soft">{notice.claimant.email}</span>
          {notice.claimant.org && (
            <span className="font-mono text-[11px] text-faint">{notice.claimant.org}</span>
          )}
          <span className="mt-1 font-mono text-[10px] text-ghost">signed: {notice.signature}</span>
        </div>

        <div className="flex flex-col gap-1">
          <span className="meta-caps text-faint">Track</span>
          {notice.track ? (
            <>
              <Link to={`/tracks/${notice.track.id}`} className="text-sm text-ink hover:underline">
                {notice.track.title}
              </Link>
              <Link
                to={`/${notice.track.owner}`}
                className="font-mono text-[11px] text-soft hover:text-ink hover:underline"
              >
                @{notice.track.owner}
              </Link>
            </>
          ) : (
            // The track was deleted after the claim. The notice stays, because
            // it is the record that the claim was made and answered.
            <span className="text-sm text-faint">Deleted since.</span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <span className="meta-caps text-faint">The work claimed</span>
        <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-text-2">
          {notice.work_description}
        </p>
      </div>

      {notice.counter && (
        <div className="flex flex-col gap-1.5 rounded-[var(--radius-control)] border border-fresh-edge bg-fresh-wash p-3.5">
          <span className="meta-caps text-fresh">
            Counter-notice{notice.counter.forwarded_at ? ' · forwarded' : ' · not yet forwarded'}
          </span>
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-text-2">
            {notice.counter.statement}
          </p>
        </div>
      )}

      {notice.resolution_note && (
        <p className="font-mono text-[11px] leading-relaxed text-ghost">
          note · {notice.resolution_note}
        </p>
      )}

      {canAction && notice.status !== 'withdrawn' && notice.status !== 'rejected' && (
        <div className="flex flex-col gap-3 border-t border-line pt-4">
          <input
            value={note}
            maxLength={1000}
            data-dmca-note={notice.id}
            placeholder={open ? 'Why (required to take it down)' : 'Note (optional)'}
            onChange={(event) => setNote(event.target.value)}
            className="rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm text-ink placeholder:text-faint focus:border-line-strong"
          />

          {decide.isError && (
            <Alert tone="error">
              {decide.error instanceof ApiError ? decide.error.message : 'That did not work.'}
            </Alert>
          )}

          <div className="flex flex-wrap items-center gap-2.5">
            {open && (
              <>
                <Button
                  variant="danger"
                  loading={decide.isPending}
                  data-dmca-action={notice.id}
                  onClick={() => decide.mutate('action')}
                >
                  Take it down
                </Button>
                <label className="flex cursor-pointer items-center gap-2 text-xs text-soft">
                  <input
                    type="checkbox"
                    checked={strike}
                    onChange={(event) => setStrike(event.target.checked)}
                    className="h-3.5 w-3.5 accent-[var(--color-signal)]"
                  />
                  with a strike
                </label>
                <Button variant="secondary" onClick={() => decide.mutate('reject')}>
                  Not a valid claim
                </Button>
              </>
            )}

            {notice.counter && !notice.counter.forwarded_at && (
              <Button variant="secondary" data-dmca-forward={notice.id} onClick={() => decide.mutate('forward')}>
                Mark as forwarded to claimant
              </Button>
            )}

            {(notice.status === 'actioned' || notice.status === 'counter_filed') && (
              <Button
                variant="secondary"
                data-dmca-restore={notice.id}
                onClick={() => decide.mutate('restore')}
              >
                Restore the track
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
